'use client';

import type { UserStatus } from '@prisma/client';
import { type ReactNode, useCallback } from 'react';
import React, { createContext, useContext, useMemo } from 'react';
import { type Permission } from '~/auth/permissions';
import { DEFAULT_TIME_ZONE } from '~/modules/class/model/class-initial-form-values';
export type UserSessionContextType = {
  userId: string;
  userEmail: string | null;
  userName: string;
  userImageUrl: string | null;
  userStatus: UserStatus;
  userTimeZone: string;
  permissions: string[];
  //
  isImpersonated: boolean;
  originalUserId: string | null;
  //
  hasPredefinedAdminRole: boolean;
  hasPredefinedStudentRole: boolean;
  hasPredefinedParentRole: boolean;
  hasPredefinedTeacherRole: boolean;
  hasPredefinedStaffRole: boolean;
  //
  isAdminInterfaceVariant: boolean;
  isClientInterfaceVariant: boolean;
};
const UserSessionContext = createContext<UserSessionContextType>({
  userId: '',
  userEmail: null,
  userName: '',
  userImageUrl: null,
  userStatus: '' as UserStatus,
  userTimeZone: DEFAULT_TIME_ZONE,
  permissions: [],
  //
  isImpersonated: false,
  originalUserId: null,
  //
  hasPredefinedAdminRole: false,
  hasPredefinedStudentRole: false,
  hasPredefinedParentRole: false,
  hasPredefinedTeacherRole: false,
  hasPredefinedStaffRole: false,
  //
  isAdminInterfaceVariant: true,
  isClientInterfaceVariant: true
});
type Props = {
  children: ReactNode;
  interfaceVariant: 'admin' | 'client';
} & Omit<UserSessionContextType, 'isClientInterfaceVariant' | 'isAdminInterfaceVariant'>;
export const UserSessionProvider = ({
  children,
  interfaceVariant,
  ...props
}: Props) => {
  const value = useMemo(() => {
    return {
      ...props,
      isAdminInterfaceVariant: interfaceVariant === 'admin',
      isClientInterfaceVariant: interfaceVariant === 'client'
    };
  }, [props, interfaceVariant]);
  return <UserSessionContext.Provider value={value} data-sentry-element="UserSessionContext.Provider" data-sentry-component="UserSessionProvider" data-sentry-source-file="user-session-context.tsx">{children}</UserSessionContext.Provider>;
};
export const useUserSession = () => {
  const context = useContext(UserSessionContext);
  const hasPermission = useCallback((permission: Permission) => context.permissions.includes(permission), [context.permissions]);
  return {
    ...context,
    hasPermission
  };
};